import {computed, inject, Injectable, signal} from '@angular/core';
import {merge, Observable, of, tap} from "rxjs";
import {Favorite} from "../models/document.model";
import {FavoriteResource} from "../resources/favorite-resource";
import {map, switchMap} from "rxjs/operators";
import {Tag} from "../models/profile.model";
import {ProfileStateService} from "../states/profile-state.service";
import {ProfileService} from "./profile.service";
import {CoreConfig} from "../core.config";
import {localeCompareDK} from "../utilities/string.utils";

@Injectable({
  providedIn: 'root'
})
export class FavoriteService {
  private favoriteResource = inject(FavoriteResource);
  private profileStateService = inject(ProfileStateService);
  private profileService = inject(ProfileService);
  private coreConfig = inject(CoreConfig);

  private isInitiated = false;

  public favorites = signal<Favorite[]>([]);
  public collections = computed(() => this.favorites().filter(favorite => favorite.doctype === 'collection'));
  public documents = computed(() => this.favorites().filter(favorite => favorite.doctype === 'document'));
  public tags = signal<Tag[]>([]);
  public tagsUsedOnDocuments = computed(() => {
    return this.tags().filter(tag => this.documents().some(document => document.tagIds.includes(tag.id)));
  });
  public tagsUsedOnCollections = computed(() => {
    return this.tags().filter(tag => this.collections().some(collection => collection.tagIds.includes(tag.id)));
  });

  public initialLoad(force: boolean = false) {
    if (this.isInitiated && !force) {
      return;
    }
    merge(
      this.getFavorites(),
      this.monitorProfileTags(),
    ).subscribe();

    this.isInitiated = true;
  }

  public isFavorite(id: string): boolean {
    return this.favorites().some(document => document.id === id);
  }

  private toggleFavorite(id: string, type: "document" | "collection") {
    let subscription;

    if (this.isFavorite(id)) {
      this.favorites.set(this.favorites().filter(document => document.id !== id));
      subscription = this.favoriteResource.endpoint(id, type, 'remove');
    } else {
      this.favorites.set([...this.favorites(), {id, type} as Favorite]);
      subscription = this.favoriteResource.endpoint(id, type, 'create');
    }
    subscription.subscribe(() => this.getFavorites().subscribe());
  }

  public getFavorites(): Observable<Favorite[]> {
    return this.favoriteResource.getFavorites().pipe(
      tap(favorites => this.favorites.set(favorites)),
    );
  }

  toggle(id: string, type: "document" | "collection") {
    this.toggleFavorite(id, type);
  }

  activeTags(favoriteId: string): Tag[] {
    const tags = this.favorites().find(favorite => favorite.id === favoriteId)?.tagIds ?? [];
    return this.tags().filter(tag => tags.includes(tag.id)).sort((a, b) => localeCompareDK(a.name, b.name, true));
  }

  hasTag(id: string, tagId: string) {
    return this.activeTags(id).some(activeTag => activeTag.id === tagId);
  }

  addTag(id: string, tagId: string) {
    const type = this.favorites().find(favorite => favorite.id === id)?.doctype;
    if (!type) {
      return of(false);
    }

    this.favorites().forEach(favorite => {
      if (favorite.id === id) {
        favorite.tagIds = [...favorite.tagIds, tagId];
      }
    });

    const tagIds = this.favorites().find(favorite => favorite.id === id)?.tagIds ?? [];
    return this.favoriteResource.setTags(id, type, [tagId, ...tagIds]).pipe(
      tap(() => this.favorites.set(this.favorites())),
    );
  }

  removeTag(id: string, tagId: string) {
    const type = this.favorites().find(favorite => favorite.id === id)?.doctype;
    if (!type) {
      return of(false);
    }

    const tagIds = this.favorites().find(favorite => favorite.id === id)?.tagIds ?? [];

    return this.favoriteResource.setTags(id, type, tagIds.filter(tag => tag !== tagId)).pipe(
      tap(() => this.getFavorites().subscribe()),
    );
  }

  public createTag(tagName: string, tagColor: string) {
    return this.favoriteResource.createTag(tagName.trim(), tagColor).pipe(
      tap(tagId => this.tags.set([...this.tags(), {id: tagId, name: tagName, color: tagColor}])),
      tap(() => this.refreshTags().subscribe()),
    );
  }

  private monitorProfileTags(): Observable<Tag[]> {
    return this.profileStateService.profile$.pipe(
      map(profile => profile?.tags ?? []),
      map(tags => tags.sort((a, b) => localeCompareDK(a.name, b.name, true))),
      tap(tags => this.tags.set(tags)),
    )
  }

  private refreshTags() {
    return this.profileService.refreshProfile();
  }

  deleteTag(id: string) {
    this.tags.set(this.tags().filter(tag => tag.id !== id));

    return this.favoriteResource.deleteTag(id).pipe(
      tap(() => this.refreshTags().subscribe()),
    );
  }

  updateTag(id: string, name: string, color: string) {
    this.tags.set(this.tags().map(tag => {
      if (tag.id === id) {
        return {
          ...tag,
          name: name.trim(),
          color: color
        };
      }
      return tag;
    }));

    return this.favoriteResource.updateTag(id, name, color).pipe(
      tap(() => this.refreshTags().subscribe()),
    );
  }

  addTagByName(id: string, tagName: string, tagColor: string|null = null): Observable<boolean> {
    const tagNameSanitized = tagName.trim();
    let tagId = this.tags().find(tag => tag.name.toLowerCase() === tagNameSanitized.toLowerCase())?.id ?? null;

    const color = tagColor ?? this.getSuggestedColor();

    return of(tagId).pipe(
      switchMap(tagId => {
        if (tagId) {
          return of(tagId);
        }
        return this.createTag(tagNameSanitized, color)
      }),
      switchMap(tagId => this.addTag(id, tagId)),
      tap(() => this.getFavorites().subscribe()),
      map(() => true),
    );

  }

  public getSuggestedColor(): string {
    const colors = [
      '#55efc4', '#81ecec', '#74b9ff', '#a29bfe',
      '#dfe6e9', '#ff7675', '#fd79a8', '#ff9f43',
      '#fdcb6e', '#00b894',
    ];

    const lightenedColors = [
      '#aaf7e2', '#c0f6f6', '#badcff', '#d1cdff',
      '#ffbbba', '#febcd4', '#ffcfa1', '#fee5b7',
      '#80dcca'
    ];
    lightenedColors.sort(() => Math.random() - 0.5);

    // Get all used colors
    const usedColors = this.tags().map(tag => tag.color);

    // Find unused color from lightenedColors
    const unusedColor = lightenedColors.find(color => !usedColors.includes(color));

    return unusedColor ?? lightenedColors[0];

  }

  canBeTagged(id: string) {
    return this.isTaggingEnabled()
      && this.isFavorite(id);
  }

  isTaggingEnabled() {
    return this.profileStateService.isLoggedIn()
      && this.coreConfig.dominoConfig.usefavoritetags;
  }

  getTagByName(value: string) {
    const sanitizedValue = value.trim().toLowerCase();
    return this.tags().find(tag => tag.name.toLowerCase() === sanitizedValue);
  }
}
